import { computed, ref, shallowRef, watch } from "vue"
import { useRoute } from "vue-router"

import { type BasePresetColumn } from "@/types"
import { useSessionStore } from "@/store/session"
import useTablesStateStore from "@/store/tablesState"

type BasePreset<T> = {
  table: T
  columns: string[]
  id: string
  isGlobal: boolean
  isGlobalDefault: boolean
  isUserDefault: boolean
  name: string
}

export function useTablePresetConfigurationShared<
  TTable extends string,
  TTableCol extends string,
  TPresetColumn extends BasePresetColumn<TTableCol>,
  TPreset extends BasePreset<TTable>,
>(table: TTable | undefined, columnDefs: TPresetColumn[], listRouteName: string | string[]) {
  const route = useRoute()
  const sessionStore = useSessionStore()
  const tablesStateStore = useTablesStateStore()

  const listQueryVariables = computed(() => ({ table }))

  const listQueryOptions = computed(() => {
    const matchedRouteNames = route.matched.reduce<string[]>(
      (acc, next) => (acc.push(<string>next.name), acc),
      []
    )

    return {
      enabled:
        !!table &&
        (typeof listRouteName === "string"
          ? matchedRouteNames.includes(listRouteName)
          : matchedRouteNames.some((route) => listRouteName.includes(route))),
    }
  })

  const selectedColumnKeys = ref<Set<string>>(new Set())
  const columns = computed(() => columnDefs.filter(columnsFilter))
  const selectedColumns = computed({
    get: () =>
      [...selectedColumnKeys.value]
        .map((key) => columns.value.find((c) => c.key === key))
        .filter((c) => !!c),
    set: (columns) => {
      selectedColumnKeys.value = new Set(columns.map((c) => c.key))
    },
  })

  const isPresetLoaded = ref(columnDefs.length === 0)
  const selectedPresetId = ref<string | undefined>()
  const presets = shallowRef<TPreset[]>([])
  const selectedPreset = computed({
    get: () => presets.value.find((p) => p.id === selectedPresetId.value),
    set: (preset) => (selectedPresetId.value = preset?.id),
  })

  watch(
    presets,
    () => {
      if (selectedPresetId.value && !presets.value?.find((p) => p.id === selectedPresetId.value))
        selectedPresetId.value = undefined

      if ((presets.value ?? []).length === 0 || selectedPresetId.value) return

      const defaultColumnsPreset =
        presets.value.find((p) => p.isUserDefault) ?? presets.value.find((p) => p.isGlobalDefault)

      if (defaultColumnsPreset) selectedPreset.value = defaultColumnsPreset
    },
    { immediate: true }
  )

  const initialized = ref(false)

  watch(selectedPreset, (preset, prevPreset) => {
    if (!initialized.value) {
      if (table) {
        const cachedKeys = tablesStateStore.get(table)?.filterPresetKeys
        if (cachedKeys) {
          selectedColumnKeys.value = new Set([...cachedKeys])
        }
      } else {
        selectedColumnKeys.value = new Set(columns.value.map((c) => c.key))
      }
      initialized.value = true
    }

    if (!preset) return

    const shouldInit = !prevPreset && (!table || !tablesStateStore.get(table)?.filterPresetKeys)
    const shouldUpdate = !!prevPreset && preset.id !== prevPreset.id

    if (shouldInit || shouldUpdate) {
      const keys = preset.columns.flatMap((key) => columnDefs.find((c) => c.key === key)?.key ?? [])
      selectedColumnKeys.value = new Set(keys)
    }
  })

  watch(selectedColumnKeys, (value) => {
    if (table) {
      tablesStateStore.set(table, { columnPresetKeys: value })
    }
  })

  function nameExists(name: string, exclude: TPreset[] = []) {
    const excludeIds = exclude.map((p) => p.id)
    return presets.value.some((p) => !excludeIds.includes(p.id) && p.name === name)
  }

  function toggleColumn(column: TPresetColumn) {
    if (![...selectedColumnKeys.value].some((key) => key === column.key))
      selectedColumnKeys.value.add(column.key)
    else if (
      [...selectedColumnKeys.value].filter((key) => columns.value.some((c) => c.key === key))
        .length > 1
    )
      selectedColumnKeys.value = new Set(
        [...selectedColumnKeys.value].filter((key) => key !== column.key)
      )
  }

  function setSelectedColumns(columns: TPresetColumn[]) {
    selectedColumns.value = columns
  }

  function setSelectedColumnsPreset(preset?: TPreset) {
    selectedPreset.value = preset
  }

  const columnsFilter = (col: BasePresetColumn<TTableCol>) => {
    const { active, requiredPermissions } = col ?? {}

    let hasPermissions = true
    if (requiredPermissions) hasPermissions = sessionStore?.hasRoles?.(requiredPermissions) ?? false

    let isActive = true
    if (typeof active === "function") isActive = active()
    else if (typeof active === "boolean") isActive = active

    return isActive && hasPermissions
  }

  return {
    listQueryVariables,
    listQueryOptions,

    initialized,

    presets,
    isPresetLoaded,
    selectedPreset,
    selectedPresetId,

    columns,
    selectedColumnKeys,
    selectedColumns,

    nameExists,
    toggleColumn,
    setSelectedColumns,
    setSelectedColumnsPreset,

    columnsFilter,
  }
}
